import { memo, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { ReactSVG } from "react-svg";
import CustomInput from "../../../../common/CustomInput";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { vaccineDoseTypeOptions } from "../clinical-assessment/data";
import CommonSelect from "../../services/select/common-creatable-select";

const INPUT_CLASS_NAME =
  "text-sm !h-full !border-0 !p-0 !rounded-none focus-within:!border-0 focus-within:!border-t-0 focus-within:!border-b-0 focus-within:!border-l-0 focus-within:!border-r-0 focus-within:!shadow-none !w-full";
const GROUP_CONTAINER_CLASS =
  "flex w-full flex-col gap-4 border-b border-gray-100 pb-5 last:border-b-0";
const GROUP_HEADER_CLASS =
  "flex w-full items-center gap-3 rounded-[4px] border border-gray-100 pr-[5px]";
const ROW_CLASS = "flex w-full items-center gap-3";

const isSingleDoseValue = (value) => {
  const t = String(value || "").trim().toLowerCase();
  return t === "single dose" || t === "one dose" || t === "single";
};

const getSafeVaccineValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    return value.label || value.value || value.name || "";
  }
  return "";
};

const DeleteIconButton = memo(function DeleteIconButton({ onClick, ariaLabel }) {
  return (
    <div className="flex-shrink-0 !w-[50px] !h-[70px] flex items-end justify-center border border-red-50 bg-red-50 rounded-tr rounded-br rounded-tl-0 rounded-bl-0">
      <button
        onClick={onClick}
        className="p-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
        aria-label={ariaLabel}
      >
        <ReactSVG src="/icons/trash-delete.svg" className="w-5 h-5" />
      </button>
    </div>
  );
});

DeleteIconButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const DateInputWrapper = memo(function DateInputWrapper({
  children,
  showDelete,
  onDelete,
  ariaLabel,
  className = "",
}) {
  return (
    <div className={`flex items-center gap-0 ${className}`}>
      <div className="flex flex-col items-start !h-[70px] border border-gray-100 rounded-tl rounded-bl rounded-tr-0 rounded-br-0 p-2 flex-1 min-w-0">
        {children}
      </div>
      {showDelete && <DeleteIconButton onClick={onDelete} ariaLabel={ariaLabel} />}
    </div>
  );
});

DateInputWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  showDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

const VaccineRow = memo(function VaccineRow({
  groupName,
  index,
  vaccine,
  vaccineDoseTypeSelectOptions,
  getDoseTypeValue,
  getVaccineDoseTypeLabel,
  onDoseTypeChange,
  onDelete,
  onDateChange,
}) {
  const doseTypeValue = getDoseTypeValue(vaccine);
  return (
    <div key={`${groupName}-${vaccine.seriesNumber}-${index}`} className={ROW_CLASS}>
      <CommonSelect
        label="Vaccine Dose Type"
        options={vaccineDoseTypeSelectOptions}
        value={{
          value: doseTypeValue,
          label: getVaccineDoseTypeLabel(doseTypeValue),
        }}
        onChange={(value) => onDoseTypeChange(index, value?.value || "")}
        className="w-[244px] [&>div]:!h-[70px] [&>div]:!bg-white [&>div]:!border-gray-100"
        labelClassName="!text-[12px] !font-normal !leading-[18px] !text-gray-800"
        isRequired
      />
      <div className="flex h-[70px] w-[150px] flex-col gap-1 rounded-[4px] border border-gray-100 bg-white px-3 py-3">
        <p className="text-[12px] font-normal leading-[18px] text-gray-800">
          Series Number *
        </p>
        <p className="truncate text-base font-normal leading-6 text-gray-800">
          {vaccine.seriesNumber || "Single Dose"}
        </p>
      </div>
      <DateInputWrapper
        showDelete
        onDelete={() => onDelete(index)}
        ariaLabel="Delete vaccine appointment"
        className="min-w-0 flex-1"
      >
        <CustomInput
          name={`Follow up date ${index}`}
          className={INPUT_CLASS_NAME}
          label="Appointment Date & Time"
          type="date"
          showTime={true}
          value={vaccine.bookingDate || ""}
          onChange={(value) => onDateChange(index, value)}
          required
        />
      </DateInputWrapper>
    </div>
  );
});

VaccineRow.propTypes = {
  groupName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  vaccine: PropTypes.object.isRequired,
  vaccineDoseTypeSelectOptions: PropTypes.array.isRequired,
  getDoseTypeValue: PropTypes.func.isRequired,
  getVaccineDoseTypeLabel: PropTypes.func.isRequired,
  onDoseTypeChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
};

const VaccineBookingPopup = ({ 
  selectedVaccinesForAppt, 
  onSave, 
  onClose,
  comingFromEdit = false,
  originalReduxVaccines = [], // Original vaccines from Redux for recovery
}) => {
  const [originalValidNames, setOriginalValidNames] = useState(new Map());
  
  useEffect(() => {
    setOriginalValidNames(prev => {
      const names = new Map(prev);
      selectedVaccinesForAppt.forEach((v, i) => {
        if (!names.has(i)) {
          const n = v?.name;
          if (typeof n === 'string' && n !== '[object Object]') names.set(i, n);
          else if (typeof n === 'object' && n?.name && typeof n.name === 'string') names.set(i, n.name);
        }
      });
      return names;
    });
  }, [selectedVaccinesForAppt]);
  
  const [bookSameAppointment, setBookSameAppointment] = useState(true);
  const [localVaccines, setLocalVaccines] = useState([]);

  const getVaccineDoseTypeLabel = useCallback((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "initial" || normalized === "initial dose") {
      return "Initial (Series)";
    }
    if (normalized === "booster" || normalized === "booster dose") {
      return "Booster";
    }
    if (normalized === "one dose") {
      return "Single Dose";
    }
    return (
      vaccineDoseTypeOptions.find((option) => option.value === value)?.label ||
      value ||
      "Single Dose"
    );
  }, []);

  const getOrdinal = useCallback((number) => {
    const value = Number(number);
    const suffix =
      value % 10 === 1 && value % 100 !== 11
        ? "st"
        : value % 10 === 2 && value % 100 !== 12
          ? "nd"
          : value % 10 === 3 && value % 100 !== 13
            ? "rd"
            : "th";
    return `${value}${suffix}`;
  }, []);

  const getBoosterSeriesLabel = useCallback(
    (number) => `${getOrdinal(number)} Booster`,
    [getOrdinal]
  );

  const getGroupKey = useCallback(
    (v) => v?.parentVaccineName || v?.name || "",
    []
  );

  const buildSeriesRow = useCallback(
    (template, bookingDate, idx, total, mode) => {
      const isSingle = mode === "single";
      const isBooster = mode === "booster";
      return {
        ...template,
        bookingDate,
        vaccineDoseType: isSingle
          ? "Single Dose"
          : isBooster || idx > 0
            ? "Booster Dose"
            : "Initial",
        appointmentDoseType: isSingle
          ? "Single Dose"
          : isBooster || idx > 0
            ? "Booster"
            : "Initial (Series)",
        seriesNumber: isSingle
          ? "Single Dose"
          : isBooster
            ? getBoosterSeriesLabel(idx + 1)
            : idx === 0
              ? "Initial Dose"
              : getBoosterSeriesLabel(idx),
        totalNumberOfVaccineDoses: isSingle ? 1 : total,
        parentVaccineName: template.parentVaccineName || template.name,
      };
    },
    [getBoosterSeriesLabel]
  );

  /** After add/remove rows, normalize series labels and sync totalNumberOfVaccineDoses per group. */
  const repackAppointmentGroups = useCallback(
    (rows) => {
      const out = [];
      let i = 0;
      while (i < rows.length) {
        const key = getGroupKey(rows[i]);
        let j = i;
        while (j < rows.length && getGroupKey(rows[j]) === key) j++;
        const slice = rows.slice(i, j);
        const n = slice.length;
        const first = slice[0];
        if (n === 1) {
          const dt = String(first.vaccineDoseType || "").toLowerCase();
          const sn = String(first.seriesNumber || "").toLowerCase();
          const mode = dt.includes("single") || sn === "single dose"
            ? "single"
            : dt.includes("booster")
              ? "booster"
              : "initial";
          out.push(buildSeriesRow(first, first.bookingDate || "", 0, 1, mode));
        } else {
          const hasInitialSlot = slice.some(
            (r) =>
              String(r.vaccineDoseType || "").toLowerCase().includes("initial") ||
              String(r.seriesNumber || "").toLowerCase() === "initial dose"
          );
          const allBoosters = slice.every((r) =>
            String(r.vaccineDoseType || "").toLowerCase().includes("booster")
          );
          const mode = allBoosters && !hasInitialSlot ? "booster" : "initial";
          out.push(
            ...slice.map((r, idx) =>
              buildSeriesRow(r, r.bookingDate || "", idx, n, mode)
            )
          );
        }
        i = j;
      }
      return out;
    },
    [buildSeriesRow, getGroupKey]
  );

  const applyDoseTypeChangeToGroup = useCallback(
    (prev, anchorIndex, selectedValue) => {
      const row = prev[anchorIndex];
      if (!row) return prev;
      const key = getGroupKey(row);
      let start = anchorIndex;
      while (start > 0 && getGroupKey(prev[start - 1]) === key) start--;
      let end = anchorIndex;
      while (end < prev.length - 1 && getGroupKey(prev[end + 1]) === key) end++;
      const slice = prev.slice(start, end + 1);
      const template = { ...slice[0] };
      const groupLen = slice.length;
      const norm = String(selectedValue || "").trim().toLowerCase();
      const isInitial = norm === "initial" || selectedValue === "Initial";
      const isBooster = norm.includes("booster") || selectedValue === "Booster Dose";
      const n = isInitial
        ? Math.min(
            5,
            Math.max(
              1,
              Math.max(
                groupLen,
                Number.parseInt(String(template.totalNumberOfVaccineDoses ?? ""), 10) ||
                  groupLen
              )
            )
          )
        : isBooster
          ? Math.min(5, Math.max(1, groupLen))
          : 1;
      const mode = isInitial ? "initial" : isBooster ? "booster" : "single";
      const newSlice = Array.from({ length: n }, (_, idx) =>
        buildSeriesRow(template, slice[idx]?.bookingDate || "", idx, n, mode)
      );
      return [
        ...prev.slice(0, start),
        ...newSlice,
        ...prev.slice(end + 1),
      ];
    },
    [buildSeriesRow, getGroupKey]
  );

  const parseSeriesSelections = useCallback((value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      const n = Math.max(1, Math.floor(value));
      return Array.from({ length: n }, (_, index) =>
        getBoosterSeriesLabel(index + 1)
      );
    }
    if (Array.isArray(value)) {
      return value.map((item) => String(item?.label || item?.value || item));
    }
    const raw = String(value || "").trim();
    if (!raw) return [];
    const numericValue = Number.parseInt(raw, 10);
    if (!Number.isNaN(numericValue) && String(numericValue) === raw) {
      return Array.from({ length: numericValue }, (_, index) =>
        getBoosterSeriesLabel(index + 1)
      );
    }
    return raw
      .split(/[,;|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }, [getBoosterSeriesLabel]);

  const getPreferredVaccineValue = useCallback((currentValue, originalValue) => {
    const current = getSafeVaccineValue(currentValue);
    const original = getSafeVaccineValue(originalValue);
    if (!current) return original;
    if (isSingleDoseValue(current) && original && !isSingleDoseValue(original)) {
      return original;
    }
    return current;
  }, []);

  const normalizeVaccineName = useCallback((vaccine, depth = 0, index = -1) => {
    const readName = (currentVaccine, currentDepth, currentIndex) => {
      if (currentDepth > 3 || !currentVaccine) return "";
      if (
        typeof currentVaccine === "string" ||
        typeof currentVaccine === "number"
      ) {
        return String(currentVaccine);
      }
      const n = currentVaccine.name;
      if (typeof n === "string" && n === "[object Object]") {
        if (currentIndex >= 0 && originalValidNames.has(currentIndex)) {
          return originalValidNames.get(currentIndex);
        }
        if (
          currentDepth === 0 &&
          currentIndex >= 0 &&
          currentIndex < originalReduxVaccines.length
        ) {
          const originalVaccine = originalReduxVaccines[currentIndex];
          if (
            typeof originalVaccine?.name === "string" &&
            originalVaccine.name !== "[object Object]"
          ) {
            return originalVaccine.name;
          }
          if (typeof originalVaccine?.name === "object") {
            const recovered = readName(
              originalVaccine,
              currentDepth + 1,
              currentIndex
            );
            if (recovered && recovered !== "[object Object]") return recovered;
          }
        }
        return "";
      }
      if (typeof n === "string") return n;
      if (typeof n === "object" && n !== null) {
        if (typeof n.name === "string") return n.name;
        if (typeof n.value === "string") return n.value;
        if (typeof n.label === "string") return n.label;
        if (typeof n.name?.name === "string") return n.name.name;
        if (typeof n.name?.value === "string") return n.name.value;
        if (typeof n.name?.label === "string") return n.name.label;
        for (const k in n) {
          if (typeof n[k] === "string" && n[k] && n[k] !== "[object Object]") {
            return n[k];
          }
        }
      }
      return "";
    };

    return readName(vaccine, depth, index);
  }, [originalReduxVaccines, originalValidNames]);

  const findOriginalVaccine = useCallback((name, index) => {
    const normalizedName = String(name || "").toLowerCase();
    return (
      originalReduxVaccines.find(
        (item, itemIndex) =>
          itemIndex === index ||
          normalizeVaccineName(item, 0, itemIndex).toLowerCase() === normalizedName
      ) || {}
    );
  }, [normalizeVaccineName, originalReduxVaccines]);

  const createAppointmentRows = useCallback((vaccine, index) => {
    const name = normalizeVaccineName(vaccine, 0, index);
    const original = findOriginalVaccine(name, index);
    const selectedDoseType =
      vaccine.vaccineDoseType || vaccine.appointmentDoseType;
    const originalDoseType =
      original.vaccineDoseType || original.appointmentDoseType;
    const vaccineDoseType = getPreferredVaccineValue(
      selectedDoseType,
      originalDoseType
    );
    const currentTotalDoses = getSafeVaccineValue(vaccine.totalNumberOfVaccineDoses);
    const originalTotalDoses = getSafeVaccineValue(original.totalNumberOfVaccineDoses);
    const explicitSeriesCount =
      Number.parseInt(String(vaccine.seriesCountForPopup || ""), 10) || 0;
    const totalDoses =
      explicitSeriesCount > 0
        ? String(explicitSeriesCount)
        :
      !isSingleDoseValue(vaccineDoseType) &&
      originalTotalDoses &&
      (!currentTotalDoses || currentTotalDoses === "1")
        ? originalTotalDoses
        : currentTotalDoses || originalTotalDoses;
    const existingSeriesNumber = getSafeVaccineValue(vaccine.seriesNumber);
    const existingAppointmentDoseType = getSafeVaccineValue(
      vaccine.appointmentDoseType
    );
    let normalizedDoseType = String(vaccineDoseType || "")
      .trim()
      .toLowerCase();
    const parsedSeriesCount = Math.max(Number.parseInt(totalDoses, 10) || 1, 1);
    const boosterSeries = parseSeriesSelections(totalDoses);
    if (
      (!normalizedDoseType || isSingleDoseValue(normalizedDoseType)) &&
      Array.isArray(vaccine.selectedBoosterOrdinals) &&
      vaccine.selectedBoosterOrdinals.length > 0
    ) {
      normalizedDoseType = "booster dose";
    } else if (
      (!normalizedDoseType || isSingleDoseValue(normalizedDoseType)) &&
      parsedSeriesCount > 1
    ) {
      normalizedDoseType = "initial";
    }
    const hasExpandableSeries =
      (normalizedDoseType.includes("initial") && parsedSeriesCount > 1) ||
      (normalizedDoseType.includes("booster") && boosterSeries.length > 1);
    const hasStaleSingleDoseRow =
      !normalizedDoseType.includes("single") &&
      (isSingleDoseValue(existingSeriesNumber) ||
        isSingleDoseValue(existingAppointmentDoseType));
    const hasFreshSelectionPayload =
      (Number.parseInt(String(vaccine.seriesCountForPopup || ""), 10) || 0) > 0 ||
      (Array.isArray(vaccine.seriesLabelsForPopup) &&
        vaccine.seriesLabelsForPopup.length > 0) ||
      (Array.isArray(vaccine.selectedBoosterOrdinals) &&
        vaccine.selectedBoosterOrdinals.length > 0);
    const isAlreadyExpandedRow =
      Boolean(vaccine.parentVaccineName) &&
      (existingSeriesNumber || existingAppointmentDoseType);

    if (
      comingFromEdit &&
      !hasFreshSelectionPayload &&
      (isAlreadyExpandedRow ||
        ((existingSeriesNumber || existingAppointmentDoseType) &&
          !hasExpandableSeries &&
          !hasStaleSingleDoseRow))
    ) {
      const shouldUseSingleDoseLabel =
        isSingleDoseValue(vaccineDoseType) ||
        isSingleDoseValue(existingSeriesNumber) ||
        isSingleDoseValue(existingAppointmentDoseType);
      const totalNum =
        Number.parseInt(String(totalDoses), 10) ||
        1;
      return [
        {
          ...vaccine,
          name,
          parentVaccineName: vaccine.parentVaccineName || name,
          appointmentDoseType:
            existingAppointmentDoseType || getVaccineDoseTypeLabel(vaccineDoseType),
          seriesNumber:
            existingSeriesNumber ||
            (shouldUseSingleDoseLabel ? "Single Dose" : "Initial Dose"),
          totalNumberOfVaccineDoses: totalNum,
        },
      ];
    }

    const baseVaccine = {
      ...vaccine,
      name,
      parentVaccineName: name,
    };

    if (
      Array.isArray(vaccine.seriesLabelsForPopup) &&
      vaccine.seriesLabelsForPopup.length > 0
    ) {
      const labels = vaccine.seriesLabelsForPopup.map((x) => String(x));
      const total = labels.length;
      return labels.map((label, idx) => {
        const lower = label.toLowerCase();
        const isInitialLabel = lower === "initial dose";
        return {
          ...baseVaccine,
          vaccineDoseType: isInitialLabel ? "Initial" : "Booster Dose",
          appointmentDoseType: isInitialLabel ? "Initial (Series)" : "Booster",
          seriesNumber: label,
          totalNumberOfVaccineDoses: total,
          selectedBoosterOrdinals:
            vaccine.selectedBoosterOrdinals || undefined,
        };
      });
    }

    if (normalizedDoseType.includes("initial")) {
      return Array.from({ length: parsedSeriesCount }, (_, seriesIndex) => ({
        ...baseVaccine,
        vaccineDoseType: seriesIndex === 0 ? "Initial" : "Booster Dose",
        appointmentDoseType:
          seriesIndex === 0 ? "Initial (Series)" : "Booster",
        seriesNumber:
          seriesIndex === 0
            ? "Initial Dose"
            : getBoosterSeriesLabel(seriesIndex),
        totalNumberOfVaccineDoses: parsedSeriesCount,
      }));
    }

    if (normalizedDoseType.includes("booster")) {
      const ordinals =
        Array.isArray(vaccine.selectedBoosterOrdinals) &&
        vaccine.selectedBoosterOrdinals.length > 0
          ? [...vaccine.selectedBoosterOrdinals].sort((a, b) => a - b)
          : null;
      const seriesRows = ordinals
        ? ordinals.map((ord) => getBoosterSeriesLabel(ord))
        : boosterSeries.length
          ? boosterSeries
          : [getBoosterSeriesLabel(1)];
      const boosterTotal = seriesRows.length;
      return seriesRows.map((seriesNumber) => ({
        ...baseVaccine,
        vaccineDoseType: "Booster Dose",
        appointmentDoseType: "Booster",
        seriesNumber,
        totalNumberOfVaccineDoses: boosterTotal,
        selectedBoosterOrdinals: ordinals || undefined,
      }));
    }

    if (isSingleDoseValue(normalizedDoseType)) {
      return [
        {
          ...baseVaccine,
          vaccineDoseType: vaccineDoseType || "Single Dose",
          appointmentDoseType: "Single Dose",
          seriesNumber: "Single Dose",
          totalNumberOfVaccineDoses: 1,
        },
      ];
    }

    return [
      {
        ...baseVaccine,
        vaccineDoseType: "Initial",
        appointmentDoseType: "Initial (Series)",
        seriesNumber: "Initial Dose",
        totalNumberOfVaccineDoses: Math.max(
          Number.parseInt(String(totalDoses), 10) || 1,
          1
        ),
      },
    ];
  }, [
    comingFromEdit,
    findOriginalVaccine,
    getBoosterSeriesLabel,
    getVaccineDoseTypeLabel,
    getPreferredVaccineValue,
    normalizeVaccineName,
    parseSeriesSelections,
  ]);

  // Convert date to ISO format (YYYY-MM-DDTHH:mm)
  const ensureDateString = (dateValue) => {
    if (!dateValue) return "";
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateValue)) return dateValue;
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(dateValue)) return dateValue.replace(/\s+/, "T");
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return `${dateValue}T12:00`;
    
    if (dateValue.includes(" | ")) {
      const [datePart, timePart] = dateValue.split(" | ");
      const [time, period] = timePart.trim().split(" ");
      const [hours, minutes] = time.split(":");
      let hour24 = parseInt(hours, 10);
      if (period === "PM" && hour24 !== 12) hour24 += 12;
      else if (period === "AM" && hour24 === 12) hour24 = 0;
      return `${datePart}T${hour24.toString().padStart(2, "0")}:${minutes.padStart(2, "0")}`;
    }

    if (dateValue instanceof Date) {
      const pad = (n) => n.toString().padStart(2, "0");
      return `${dateValue.getFullYear()}-${pad(dateValue.getMonth() + 1)}-${pad(dateValue.getDate())}T${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}`;
    }

    const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
    if (isoMatch) {
      const [, year, month, day, hour = "12", minute = "00"] = isoMatch;
      return `${year}-${month}-${day}T${hour}:${minute}`;
    }
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      const pad = (n) => n.toString().padStart(2, "0");
      return `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}T${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}`;
    }
    return "";
  };

  useEffect(() => {
    const normalized = selectedVaccinesForAppt.map((v, i) => {
      const vaccineRecord =
        v && typeof v === "object"
          ? v
          : {
              name:
                typeof v === "string" || typeof v === "number"
                  ? String(v)
                  : "",
            };
      let n = normalizeVaccineName(v, 0, i);
      if (!n || n === "[object Object]")
        n =
          originalValidNames.get(i) ||
          (originalReduxVaccines[i]
            ? normalizeVaccineName(originalReduxVaccines[i], 0, i)
            : "");
      return { ...vaccineRecord, name: n || "" };
    });
    if (normalized.length > 0) {
      const vaccinesForRows = normalized.map((vaccine) => {
        if (comingFromEdit) return vaccine;
        const inferredDoseType =
          vaccine.vaccineDoseType || vaccine.appointmentDoseType || "";
        const inferredCount =
          Number.parseInt(
            String(vaccine.seriesCountForPopup || vaccine.totalNumberOfVaccineDoses || ""),
            10
          ) || 1;
        return {
          ...vaccine,
          vaccineDoseType: inferredDoseType,
          appointmentDoseType: inferredDoseType,
          totalNumberOfVaccineDoses: inferredCount,
          seriesNumber: "",
          parentVaccineName: "",
        };
      });

      const init = vaccinesForRows.flatMap((v, index) =>
        createAppointmentRows(v, index).map((row) => ({
          ...row,
          bookingDate: (row.bookingDate || "").replace("T", " "),
        }))
      );
      setLocalVaccines(init);
      setBookSameAppointment(true);
    } else {
      setLocalVaccines([]);
    }
  }, [
    selectedVaccinesForAppt,
    originalReduxVaccines,
    comingFromEdit,
    originalValidNames,
    normalizeVaccineName,
    createAppointmentRows,
  ]);

  const handleIndividualDateChange = (index, value) => {
    const updated = bookSameAppointment
      ? localVaccines.map((v) => ({ ...v, bookingDate: value || "" }))
      : [...localVaccines];
    if (!bookSameAppointment) {
      updated[index] = { ...updated[index], bookingDate: value || "" };
    }
    setLocalVaccines(updated);
  };

  const handleSave = () => {
    const packed = repackAppointmentGroups(localVaccines);
    onSave(
      packed.map((v) => {
        const rest = { ...v };
        delete rest.selectedBoosterOrdinals;
        return {
          ...rest,
          name: v.name && v.name !== "[object Object]" ? v.name : "",
          totalNumberOfVaccineDoses: Number(v.totalNumberOfVaccineDoses) || 1,
          bookingDate: v.bookingDate ? ensureDateString(v.bookingDate) : "",
        };
      })
    );
  };

  const handleDeleteVaccine = (index) => {
    setLocalVaccines((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return repackAppointmentGroups(next);
    });
  };

  const vaccineDoseTypeSelectOptions = useMemo(
    () =>
      vaccineDoseTypeOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    []
  );

  const getDoseTypeValue = (vaccine) => {
    const value = vaccine.vaccineDoseType || vaccine.appointmentDoseType;
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "booster") return "Booster Dose";
    if (normalized === "initial (series)") return "Initial";
    if (normalized === "one dose") return "Single Dose";
    return value || "Single Dose";
  };

  const groupedVaccines = useMemo(
    () =>
      localVaccines.reduce((groups, vaccine, index) => {
        const groupName = vaccine.parentVaccineName || vaccine.name || "Unknown Vaccine";
        const existingGroup = groups.find((group) => group.name === groupName);
        if (existingGroup) {
          existingGroup.rows.push({ vaccine, index });
          return groups;
        }
        return [...groups, { name: groupName, rows: [{ vaccine, index }] }];
      }, []),
    [localVaccines]
  );

  const handleDoseTypeChange = useCallback(
    (index, selectedValue) => {
      setLocalVaccines((prev) =>
        applyDoseTypeChangeToGroup(prev, index, selectedValue)
      );
    },
    [applyDoseTypeChangeToGroup]
  );

  const renderContent = useCallback(() => (
    <div>
      <div className="flex items-center border-gray-100 pt-0">
        <input
          type="checkbox"
          id="bookSameAppointment"
          checked={bookSameAppointment}
          onChange={(e) => setBookSameAppointment(e.target.checked)}
          className="mr-2 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
        />
        <label htmlFor="bookSameAppointment" className="text-gray-900 text-[16px]">
          Select to book same appointment for all vaccines
        </label>
      </div>

      <div className="mt-8 flex flex-col items-start gap-5">
        {groupedVaccines.map((group) => (
            <div key={group.name} className={GROUP_CONTAINER_CLASS}>
              <div className="flex w-full items-center gap-3 rounded-[4px] border border-gray-100 pr-[5px]">
                <div className="flex-1 px-3 py-3">
                  <p className="text-[12px] font-normal leading-[18px] text-gray-800">
                    Vaccine Name
                  </p>
                  <p className="text-base font-normal leading-6 text-gray-800">
                    {group.name}
                  </p>
                </div>
                <ReactSVG
                  src="/icons/rx-vigilance-icon.svg"
                  className="h-[50px] w-[50px] shrink-0"
                />
              </div>
              {group.rows.map(({ vaccine, index }) => (
                <VaccineRow
                  key={`${group.name}-${vaccine.seriesNumber}-${index}`}
                  groupName={group.name}
                  index={index}
                  vaccine={vaccine}
                  vaccineDoseTypeSelectOptions={vaccineDoseTypeSelectOptions}
                  getDoseTypeValue={getDoseTypeValue}
                  getVaccineDoseTypeLabel={getVaccineDoseTypeLabel}
                  onDoseTypeChange={handleDoseTypeChange}
                  onDelete={handleDeleteVaccine}
                  onDateChange={handleIndividualDateChange}
                />
              ))}
            </div>
          ))}
      </div>

      {localVaccines.length === 0 && (
        <div className="text-center py-12 text-teal-300">
          <p className="text-lg">No vaccines selected. Please add vaccines to book appointments.</p>
        </div>
      )}

      {localVaccines.length > 0 && (
        <div className="rounded-md mt-5 text-gray-600">
          <p className="text-[16px] text-gray-900">
            Booked vaccine appointments will be added to the Storyboard as reminders
          </p>
        </div>
      )}
    </div>
  ), [
    bookSameAppointment,
    groupedVaccines,
    handleDeleteVaccine,
    handleDoseTypeChange,
    handleIndividualDateChange,
    localVaccines.length,
    setBookSameAppointment,
    vaccineDoseTypeSelectOptions,
    getDoseTypeValue,
    getVaccineDoseTypeLabel,
  ]);

  return (
    <ConfirmationModalNew
      open={true}
      title="Book Vaccine Appointment(s)"
      className="!w-[876px] !max-w-[90vw]"
      content={renderContent}
      type="primary"
      confirmText="Save"
      cancelText="Cancel"
      handleClose={() => onClose(comingFromEdit, localVaccines.length === 0)}
      onCross={() => onClose(comingFromEdit, localVaccines.length === 0)}
      confirmAction={handleSave}
      hideActions={false}
      maskClosable={false}
    />
  );
};

VaccineBookingPopup.propTypes = {
  selectedVaccinesForAppt: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  comingFromEdit: PropTypes.bool,
  originalReduxVaccines: PropTypes.arrayOf(PropTypes.object),
};

export default VaccineBookingPopup;